import type { Song } from './types.ts';
import type { MessageKey } from '../i18n/messages.en.ts';
import { collateThai } from '../i18n/collation.ts';

/**
 * Catalog-search shipped its real server endpoint (`GET /api/catalog/search`, SPEC §4.3/§6.2) —
 * see `api/catalogSearch.ts`'s `searchCatalog()`, which is what `LibraryScreen` actually calls now.
 *
 * This file used to also export `fetchCatalog()`, a client-side stand-in that paged through
 * Strapi's core-CRUD `GET /api/songs` (kj-only — the wrong endpoint for a screen a `kj` role
 * should be able to browse without re-implementing server-side search) and ran the ranking/facet
 * functions below over the full result in the browser. It has been removed (Task E integration
 * pass): nothing called it any more once `LibraryScreen` moved to `searchCatalog()`, and it had no
 * dedicated test coverage to orphan. `highlightRanges()`/`normalizeText()`, the `FacetKey`/
 * `FacetFilters`/`FacetGroup`/`SortKey`/`HighlightRange` types, `SORT_OPTIONS`, and the facet label
 * maps below stay — `LibraryScreen`, `FacetPanel`, `ActiveFilterBar`, `SongRow`/`SongTile`,
 * `facetAdapter.ts` and `GuestSongRow` still import them directly, unrelated to which endpoint
 * supplies the rows. `matchSong()`/`searchAndSort()`/`applyFacetFilters()`/`buildFacetGroups()`
 * (the in-browser search/filter implementation `fetchCatalog()`'s result used to be run through)
 * are left as-is even though nothing currently calls them outside their own tests — they were not
 * named in this integration pass's scope and remain a real, tested reference implementation of
 * AC-CAT-2/3/5's rules, not a wrong-endpoint hazard the way `fetchCatalog()` was.
 */

// ---------------------------------------------------------------------------------------------
// Search (AC-CAT-2 tokenisation / AC-CAT-3 ranking, re-expressed client-side — AC-UI-3)
// ---------------------------------------------------------------------------------------------

/** Lowercase + Unicode-NFKD + strip combining marks, so `beyonce` matches `Beyoncé` (AC-CAT-2).
 * Thai/CJK have no decomposable diacritics and pass through unchanged. */
export function normalizeText(value: string): string {
  return value
    .normalize('NFKD')
    .replace(/\p{Mn}/gu, '') // strips combining marks left behind by NFKD (e.g. Beyoncé -> beyonce)
    .toLowerCase()
    .trim();
}

/** Splits on anything that is not a Unicode letter or number. Latin text yields real words;
 * space-less scripts (Thai, CJK) mostly yield one token, which is fine — the substring/compact
 * tiers below still find a match inside it. */
function wordsOf(value: string): string[] {
  return normalizeText(value)
    .split(/[^\p{L}\p{N}]+/u)
    .filter(Boolean);
}

/** Strips everything but letters/numbers (no spaces either), so `ac dc` matches `AC-DC` /
 * `AC/DC` regardless of which punctuation the title actually used. */
function compactOf(value: string): string {
  return normalizeText(value).replace(/[^\p{L}\p{N}]+/gu, '');
}

export type MatchTier = 'title-exact' | 'title-prefix' | 'artist-exact' | 'title-word' | 'artist-word' | 'any-word' | 'substring' | 'compact';

const TIER_RANK: Record<MatchTier, number> = {
  'title-exact': 0,
  'title-prefix': 1,
  'artist-exact': 2,
  'title-word': 3,
  'artist-word': 4,
  'any-word': 5,
  substring: 6,
  compact: 7,
};

export interface SongMatch {
  song: Song;
  tier: MatchTier;
}

/** AC-CAT-3's ranking formula, run client-side: exact title > title prefix > artist exact >
 * title word > artist word > any-field word > substring > punctuation-insensitive compact match.
 * Returns null on no match at all. */
export function matchSong(song: Song, rawQuery: string): SongMatch | null {
  const query = normalizeText(rawQuery);
  if (!query) return null;

  const title = normalizeText(song.title);
  const artist = normalizeText(song.artistNameCache ?? '');

  if (title === query) return { song, tier: 'title-exact' };
  if (title.startsWith(query)) return { song, tier: 'title-prefix' };
  if (artist && artist === query) return { song, tier: 'artist-exact' };
  if (wordsOf(song.title).includes(query)) return { song, tier: 'title-word' };
  if (wordsOf(song.artistNameCache ?? '').includes(query)) return { song, tier: 'artist-word' };

  const otherFieldWords = [
    ...wordsOf(song.album ?? ''),
    ...wordsOf(song.genre ?? ''),
    ...wordsOf(song.featuring ?? ''),
    ...(song.languages ?? []).flatMap(wordsOf),
    ...(song.tags ?? []).flatMap(wordsOf),
  ];
  if (otherFieldWords.includes(query)) return { song, tier: 'any-word' };

  const haystack = [title, artist, ...otherFieldWords].join(' ');
  if (haystack.includes(query)) return { song, tier: 'substring' };

  const compactQuery = compactOf(rawQuery);
  if (compactQuery.length >= 2 && compactOf(`${song.title} ${song.artistNameCache ?? ''}`).includes(compactQuery)) {
    return { song, tier: 'compact' };
  }

  return null;
}

/** MVP-296: title/artist sorting is Thai dictionary order, always — see `i18n/collation.ts`'s own
 * header for the owner's decision and why `api/` importing it is not a layering violation. */
function collate(a: string, b: string): number {
  return collateThai(a, b);
}

function toTime(iso: string | null | undefined): number {
  const t = iso ? Date.parse(iso) : NaN;
  return Number.isFinite(t) ? t : 0;
}

/** MVP-272 Phase 4b: the options carry a dictionary key, not a word. `SortControl` is the only
 * thing that renders them and it resolves each one at the render site; this module is imported by
 * pure `.ts` units under a node test runner that cannot load the provider. */
export const SORT_OPTIONS = [
  // MVP-221: the catalogue number every KJ and singer reads off the old printed book. Newest-first
  // is the default (DEFAULT_LIBRARY_PREFS) because a code is issued in order, so high = new.
  { key: 'codeDesc', labelKey: 'library.sort.codeDesc' },
  { key: 'code', labelKey: 'library.sort.code' },
  { key: 'title', labelKey: 'library.sort.title' },
  { key: 'artist', labelKey: 'library.sort.artist' },
  { key: 'added', labelKey: 'library.sort.added' },
  { key: 'played', labelKey: 'library.sort.played' },
  { key: 'duration', labelKey: 'library.sort.duration' },
  // MVP-312: a `year` option used to sit here. It was removed rather than left dangling, because
  // `SortKey` is derived from this list — dropping the row is what narrows the type and makes the
  // compiler point at every consumer (`libraryPrefs`'s own key union, `facetAdapter`'s
  // `CLIENT_TO_SERVER_SORT`, `compareSongs` below) instead of leaving a sort nothing can satisfy.
  // No song carries a year and nothing can ever fill it: every production file's container metadata
  // was stripped by an upstream ffmpeg remux, and the source filenames have no year in them.
] as const satisfies ReadonlyArray<{ key: string; labelKey: MessageKey }>;

export type SortKey = (typeof SORT_OPTIONS)[number]['key'];

/** MVP-296: `catalogId` is a zero-padded digit string (SPEC's numbering, not free text), so its
 * order is a data-format fact, not a language one — plain code-unit comparison already gives numeric
 * order, with no collation involved. This deliberately does NOT go through `collateThai()`: a real
 * `Intl.Collator` has no obligation to agree with anyone about where an unassigned/noncharacter code
 * point like the `'￿'` (U+FFFF) sentinel below belongs — it could legally treat it as ignorable or
 * reorder it, which would silently break "a codeless song sorts last" in one or both directions (see
 * `catalog.test.ts`'s codeless-sorts-last tests, which pin this against exactly that risk). Plain `<`/
 * `>` on strings is a JS-spec-guaranteed UTF-16 code-unit comparison, so U+FFFF — the highest BMP code
 * unit — is guaranteed to sort after every digit, on every engine, forever.
 */
function compareCodePoint(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

function compareSongs(a: Song, b: Song, sort: SortKey): number {
  switch (sort) {
    // Codes are zero-padded, so a plain string compare is numeric order. A codeless song sorts
    // last in both directions, mirroring the server's COALESCE trick in ranking.js.
    case 'code':
      return compareCodePoint(a.catalogId || '￿', b.catalogId || '￿') || collate(a.title, b.title);
    case 'codeDesc':
      return compareCodePoint(b.catalogId || '', a.catalogId || '') || collate(a.title, b.title);
    case 'title':
      return collate(a.titleSort || a.title, b.titleSort || b.title);
    case 'artist':
      return collate(a.artistNameCache || '', b.artistNameCache || '') || collate(a.title, b.title);
    case 'added':
      return toTime(b.addedAt) - toTime(a.addedAt);
    case 'played':
      return (b.playCount ?? 0) - (a.playCount ?? 0) || collate(a.title, b.title);
    case 'duration':
      return (b.durationMs ?? 0) - (a.durationMs ?? 0);
    // MVP-312: the `year` comparator went with the sort option above. `song.year` itself stays on
    // the model (`decadeOf` below still reads it), so this is only the retirement of an ordering
    // nobody can be offered — not a claim that the field is gone.
    default:
      return 0;
  }
}

/** No query -> every song, sorted by `sort`. A query -> only matches, ranked by AC-CAT-3's tier
 * order and tie-broken by play count, then rating, then title collation (AC-UI-3). */
export function searchAndSort(songs: Song[], query: string, sort: SortKey): Song[] {
  const q = query.trim();
  if (!q) return [...songs].sort((a, b) => compareSongs(a, b, sort));

  const matches: SongMatch[] = [];
  for (const song of songs) {
    const match = matchSong(song, q);
    if (match) matches.push(match);
  }

  matches.sort((a, b) => {
    const tierDiff = TIER_RANK[a.tier] - TIER_RANK[b.tier];
    if (tierDiff !== 0) return tierDiff;
    return (
      (b.song.playCount ?? 0) - (a.song.playCount ?? 0) ||
      (b.song.ratingAvg ?? 0) - (a.song.ratingAvg ?? 0) ||
      collate(a.song.titleSort || a.song.title, b.song.titleSort || b.song.title)
    );
  });

  return matches.map((m) => m.song);
}

export interface HighlightRange {
  start: number;
  end: number;
}

/** Case-insensitive substring ranges of `query` inside `text`, for bolding matched characters in
 * results (AC-UI-3's "highlighted matches"). Purely a rendering aid — matchSong() decides whether
 * a song matched at all; a song can match via a diacritic-folded/compact tier with no literal
 * substring to underline, and this correctly returns no ranges for that case rather than guessing. */
export function highlightRanges(text: string, rawQuery: string): HighlightRange[] {
  const query = rawQuery.trim();
  if (!query) return [];
  const lowerText = text.toLowerCase();
  const lowerQuery = query.toLowerCase();
  const ranges: HighlightRange[] = [];
  let from = 0;
  for (;;) {
    const idx = lowerText.indexOf(lowerQuery, from);
    if (idx === -1) break;
    ranges.push({ start: idx, end: idx + lowerQuery.length });
    from = idx + lowerQuery.length;
  }
  return ranges;
}

// ---------------------------------------------------------------------------------------------
// Facets (AC-CAT-5, re-expressed client-side — AC-UI-6)
// ---------------------------------------------------------------------------------------------

export type FacetKey = 'kind' | 'genre' | 'language' | 'decade' | 'lyrics' | 'duration' | 'resolution' | 'played' | 'breakMusic' | 'guideVocal';

/** Selected value ids per facet group. A song matches a group when it has ANY of the group's
 * selected values (OR within a group); groups combine with AND (SPEC AC-CAT-6's "AND/OR groups"
 * is the full saved-search language — this is the simpler always-on browse behaviour AC-CAT-5
 * describes for facet chips). */
export type FacetFilters = Partial<Record<FacetKey, string[]>>;

export interface FacetValue {
  value: string;
  label: string;
  count: number;
  active: boolean;
}

export interface FacetGroup {
  key: FacetKey;
  label: string;
  values: FacetValue[];
}

export function decadeOf(song: Song): string {
  if (!song.year) return 'unknown';
  return `${Math.floor(song.year / 10) * 10}s`;
}

export function durationBucketOf(song: Song): string {
  if (!song.durationMs) return 'unknown';
  const min = song.durationMs / 60_000;
  if (min < 2) return 'under2';
  if (min < 4) return '2to4';
  if (min < 6) return '4to6';
  return 'over6';
}

/** MVP-431 widened the parameter from `Song` to anything carrying a height, so the withheld
 * screen's own row type can be bucketed by this function rather than by a second copy of the
 * ladder that could drift from the facet's. */
export function resolutionBucketOf(song: { height?: number | null }): string {
  const h = song.height ?? 0;
  if (!h) return 'unknown';
  if (h < 480) return 'sd';
  if (h < 720) return '480p';
  if (h < 1080) return '720p';
  if (h < 2160) return '1080p';
  return '4k';
}

/** A resolution bucket id written the way the format itself is spelled. The buckets above are ids,
 * not labels: `sd` and `4k` are lowercase because they are keys, and "SD"/"4K" is how an operator
 * reads those two formats. `480p`/`720p`/`1080p` already read correctly and pass through.
 * Deliberately not translated — a format name is the same word in both dictionaries. */
export function resolutionLabel(bucket: string): string {
  if (bucket === 'sd') return 'SD';
  if (bucket === '4k') return '4K';
  return bucket;
}

export function hasLyrics(song: Song): boolean {
  return Boolean(song.lyricTracks && song.lyricTracks.length > 0);
}

/**
 * A language code's display name (e.g. `ja` -> "Japanese" / "ญี่ปุ่น"), falling back to the raw code
 * if `Intl.DisplayNames` throws (an unrecognised code, or an environment that lacks it). Exported
 * for `features/library/facetAdapter.ts`'s language facet, which used to carry an independent,
 * byte-for-byte copy of this same function — see that module's own comment.
 *
 * MVP-291: `locale` is a resolved BCP-47 tag rather than the app's `Locale` union, so this `api/`
 * module needs no import from `i18n/`. Callers inside the app pass `displayLocale(locale)`
 * (`i18n/formats.ts`); it used to read `navigator.language`, which is the browser's setting and not
 * the one the KJ chose in Settings.
 */
export function languageDisplayName(code: string, locale: string): string {
  try {
    return new Intl.DisplayNames([locale], { type: 'language' }).of(code) ?? code;
  } catch {
    return code;
  }
}

function valuesOf(song: Song, key: FacetKey): string[] {
  switch (key) {
    case 'kind':
      return [song.kind];
    case 'genre':
      return song.genre ? [song.genre] : ['unknown'];
    case 'language':
      return song.languages && song.languages.length > 0 ? song.languages : ['unknown'];
    case 'decade':
      return [decadeOf(song)];
    case 'lyrics':
      return [hasLyrics(song) ? 'has' : 'none'];
    case 'duration':
      return [durationBucketOf(song)];
    case 'resolution':
      return [resolutionBucketOf(song)];
    case 'played':
      return [(song.playCount ?? 0) > 0 ? 'played' : 'never'];
    default:
      return [];
  }
}

function matchesFacetGroup(song: Song, key: FacetKey, selected: string[] | undefined): boolean {
  if (!selected || selected.length === 0) return true;
  const values = valuesOf(song, key);
  return selected.some((v) => values.includes(v));
}

/** AND across groups, OR within a group. */
export function applyFacetFilters(songs: Song[], filters: FacetFilters): Song[] {
  const keys = Object.keys(filters) as FacetKey[];
  if (keys.length === 0) return songs;
  return songs.filter((song) => keys.every((key) => matchesFacetGroup(song, key, filters[key])));
}

/** MVP-390: `buildFacetGroups()` and its `FACET_DEFS` label table lived here since before the
 * server counted facets. MVP-291's own comment already recorded that the app no longer called it —
 * the library reads server counts through `features/library/facetAdapter.ts`, which is the path
 * that gets the app's locale. What was left was eight English labels ("Type", "Genre",
 * "Unspecified", "Never played") with one caller: their own test. Deleted rather than translated. */

